import type { AxiosError } from "axios";
import axios from "axios";

const unsafeMethods = new Set(["POST", "PUT", "PATCH", "DELETE"]);

function readCookie(name: string): string {
  const prefix = `${name}=`;
  const entry = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(prefix));

  if (!entry) return "";

  const value = entry.slice(prefix.length);
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function isSameOrigin(input: RequestInfo | URL): boolean {
  if (typeof input === "string" || input instanceof URL) {
    return (
      new URL(input, window.location.origin).origin === window.location.origin
    );
  }

  return (
    new URL(input.url, window.location.origin).origin === window.location.origin
  );
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  return (
    init?.method ?? (input instanceof Request ? input.method : "GET")
  ).toUpperCase();
}

function withCsrfHeaders(
  input: RequestInfo | URL,
  init: RequestInit = {},
): RequestInit {
  const headers = new Headers(
    init.headers ?? (input instanceof Request ? input.headers : undefined),
  );
  const xsrf = readCookie("XSRF-TOKEN");
  headers.delete("X-CSRF-TOKEN");

  if (xsrf && !headers.has("X-XSRF-TOKEN")) {
    headers.set("X-XSRF-TOKEN", xsrf);
  }

  return {
    ...init,
    credentials: init.credentials ?? "same-origin",
    headers,
  };
}

async function refreshCsrfCookie(fetcher: typeof window.fetch): Promise<void> {
  await fetcher("/sanctum/csrf-cookie", {
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
  });
}

function isAxiosSameOrigin(url?: string): boolean {
  if (!url) return true;

  return new URL(url, window.location.origin).origin === window.location.origin;
}

function axiosMethod(method?: string): string {
  return (method ?? "GET").toUpperCase();
}

export function installAxiosCsrfRetry(): void {
  axios.defaults.withCredentials = true;
  axios.defaults.xsrfCookieName = "XSRF-TOKEN";
  axios.defaults.xsrfHeaderName = "X-XSRF-TOKEN";

  axios.interceptors.request.use((config) => {
    if (
      unsafeMethods.has(axiosMethod(config.method)) &&
      isAxiosSameOrigin(config.url)
    ) {
      const xsrf = readCookie("XSRF-TOKEN");

      if (xsrf) {
        config.headers.delete?.("X-CSRF-TOKEN");
        config.headers.set?.("X-XSRF-TOKEN", xsrf);
      }
    }

    return config;
  });

  axios.interceptors.response.use(undefined, async (error: AxiosError) => {
    const config = error.config;
    const retryConfig = config as typeof config & { _csrfRetried?: boolean };

    if (
      error.response?.status !== 419 ||
      !config ||
      retryConfig._csrfRetried ||
      !unsafeMethods.has(axiosMethod(config.method)) ||
      !isAxiosSameOrigin(config.url)
    ) {
      return Promise.reject(error);
    }

    retryConfig._csrfRetried = true;
    await refreshCsrfCookie(window.fetch.bind(window));

    const xsrf = readCookie("XSRF-TOKEN");
    if (xsrf) {
      retryConfig.headers?.delete?.("X-CSRF-TOKEN");
      retryConfig.headers?.set?.("X-XSRF-TOKEN", xsrf);
    }

    return axios(retryConfig);
  });
}

export function installCsrfFetchRetry(): void {
  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const method = requestMethod(input, init);

    if (!unsafeMethods.has(method) || !isSameOrigin(input)) {
      return nativeFetch(input, init);
    }

    const firstResponse = await nativeFetch(
      input,
      withCsrfHeaders(input, init),
    );

    if (firstResponse.status !== 419) {
      return firstResponse;
    }

    await refreshCsrfCookie(nativeFetch);

    return nativeFetch(input, withCsrfHeaders(input, init));
  };
}
